import React, { useState, useEffect } from 'react';
import { BookOpen, Users, Award, TrendingUp, CheckCircle, Clock, Target, Zap, Menu, X } from 'lucide-react';

const PersonalCoaching = () => {
  const [mobileMenu, setMobileMenu] = useState(false);

  const programs = [
    {
      title: "IIT-JEE Coaching",
      grades: "Class 8-12",
      description: "Comprehensive preparation for JEE Main & Advanced with personalized mentorship from IIT alumni",
      features: ["One-on-one doubt clearing", "Weekly progress tracking", "Mock test analysis", "Study plan customization"],
      duration: "1-4 Years",
      batch: "Individual Sessions"
    },
    {
      title: "NEET Coaching",
      grades: "Class 11-12, Droppers",
      description: "Specialized medical entrance preparation with focus on conceptual clarity and problem-solving",
      features: ["Personalized study schedules", "Biology practical sessions", "Previous year analysis", "Interview preparation"],
      duration: "1-2 Years",
      batch: "Individual Sessions"
    },
    {
      title: "School Excellence Program",
      grades: "Class 6-10",
      description: "Building strong fundamentals for board exams and competitive preparation",
      features: ["CBSE/ICSE/State board coverage", "Concept strengthening", "Regular assessments", "Parent-teacher meetings"],
      duration: "6 Months - 2 Years",
      batch: "Individual Sessions"
    },
    {
      title: "IIT-JAM Preparation",
      grades: "B.Sc/B.Tech Students",
      description: "Focused coaching for IIT-JAM with subject-specific expertise",
      features: ["Mathematics/Physics/Chemistry", "Previous paper solutions", "Research methodology", "Interview guidance"],
      duration: "8-12 Months",
      batch: "Individual Sessions"
    },
    {
      title: "GATE Preparation",
      grades: "Engineering Students",
      description: "Strategic preparation for GATE with branch-specific mentorship",
      features: ["All engineering branches", "Numerical emphasis", "Time management", "PSU preparation"],
      duration: "10-14 Months",
      batch: "Individual Sessions"
    },
    {
      title: "Study Abroad Counseling",
      grades: "Undergraduate/Graduate",
      description: "Complete guidance for international education from IIT alumni studying/working abroad",
      features: ["University selection", "SOP/LOR guidance", "Visa assistance", "Scholarship advice"],
      duration: "3-6 Months",
      batch: "Individual Sessions"
    }
  ];

  const whyPersonalCoaching = [
    {
      icon: Target,
      title: "Customized Learning Path",
      description: "Every student has unique strengths and weaknesses. Our personal coaching adapts to your learning style, pace, and goals."
    },
    {
      icon: Users,
      title: "IIT Alumni Mentorship",
      description: "Learn from those who've walked the path. Our mentors from IIT and premier institutions provide insider strategies and motivation."
    },
    {
      icon: TrendingUp,
      title: "Progress Tracking",
      description: "Weekly assessments, detailed analytics, and regular feedback ensure you're always improving and staying on track."
    },
    {
      icon: Clock,
      title: "Flexible Scheduling",
      description: "Choose session timings that work for you. We accommodate your school schedule, extracurriculars, and personal commitments."
    }
  ];

  const testimonials = [
    {
      name: "Rahul Sharma",
      exam: "IIT-JEE 2024 - AIR 247",
      text: "The personal coaching at Olewin Academy transformed my preparation. My mentor helped me identify weak areas and create a strategy that worked perfectly for me."
    },
    {
      name: "Priya Menon",
      exam: "NEET 2024 - AIR 512",
      text: "One-on-one sessions helped me overcome my fear of organic chemistry. The personalized attention made all the difference in my NEET preparation."
    },
    {
      name: "Amit Patel",
      exam: "GATE 2024 - AIR 89",
      text: "Being a working professional, I needed flexible timings. Personal coaching gave me that flexibility along with expert guidance from IIT alumni."
    }
  ];

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
        color: 'white',
        padding: '100px 20px 80px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(88, 169, 91, 0.1) 0%, transparent 50%)',
          animation: 'pulse 4s ease-in-out infinite'
        }} />
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '40px', alignItems: 'center' }}>
            <div>
              <h1 style={{
                fontSize: 'clamp(2rem, 5vw, 3rem)',
                fontWeight: 'bold',
                marginBottom: '1.5rem',
                animation: 'fadeInUp 0.8s ease-out',
                lineHeight: '1.2'
              }}>
                Personal Coaching
                <span style={{ color: '#58A95B', display: 'block' }}>Tailored for Your Success</span>
              </h1>
              <p style={{
                fontSize: 'clamp(1rem, 2vw, 1.2rem)',
                marginBottom: '2rem',
                lineHeight: '1.8',
                animation: 'fadeInUp 1s ease-out'
              }}>
                Experience one-on-one mentorship from IIT alumni and subject matter experts. 
                Get personalized study plans, doubt-clearing sessions, and strategic guidance 
                designed exclusively for your academic goals.
              </p>
              <button style={{
                backgroundColor: '#58A95B',
                border: 'none',
                padding: '15px 40px',
                fontSize: '1.1rem',
                fontWeight: '600',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                animation: 'fadeInUp 1.2s ease-out'
              }}
              onMouseOver={(e) => e.target.style.transform = 'translateY(-3px)'}
              onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}>
                Book Free Consultation
              </button>
            </div>
            <div style={{ textAlign: 'center', animation: 'float 3s ease-in-out infinite' }}>
              <BookOpen size={200} color="#58A95B" strokeWidth={1.5} />
            </div>
          </div>
        </div>
      </section>

      {/* Why Personal Coaching */}
      <section style={{ padding: '80px 20px', backgroundColor: '#f8f9fa' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
            fontWeight: 'bold',
            marginBottom: '3rem',
            color: '#000000'
          }}>
            Why Choose <span style={{ color: '#58A95B' }}>Personal Coaching?</span>
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '30px'
          }}>
            {whyPersonalCoaching.map((item, idx) => {
              const IconComponent = item.icon;
              return (
                <div key={idx} style={{
                  backgroundColor: 'white',
                  borderRadius: '15px',
                  padding: '30px 20px',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  animation: `fadeInUp ${0.5 + idx * 0.2}s ease-out`,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.boxShadow = '0 15px 40px rgba(88, 169, 91, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.05)';
                }}>
                  <IconComponent size={40} color="#58A95B" style={{ marginBottom: '20px' }} />
                  <h5 style={{ fontWeight: '600', marginBottom: '1rem', color: '#000' }}>{item.title}</h5>
                  <p style={{ color: '#666', lineHeight: '1.6', margin: 0 }}>{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section style={{ padding: '80px 20px', backgroundColor: '#ffffff' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
            fontWeight: 'bold',
            marginBottom: '1rem',
            color: '#000000',
          }}>
            Our <span style={{ color: '#58A95B' }}>Personal Coaching Programs</span>
          </h2>
          <p style={{ textAlign: 'center', fontSize: '1.1rem', color: '#666', marginBottom: '3rem' }}>
            Comprehensive coaching across all major competitive exams and academic levels
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px'
          }}>
            {programs.map((program, idx) => (
              <div key={idx} style={{
                backgroundColor: 'white',
                borderRadius: '15px',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                boxShadow: '0 5px 20px rgba(0, 0, 0, 0.58)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 15px 40px rgba(88, 169, 91, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 5px 20px rgba(0,0,0,0.08)';
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #58A95B 0%, #4a8f4d 100%)',
                  padding: '30px 20px',
                  color: 'white'
                }}>
                  <h4 style={{ fontWeight: 'bold', marginBottom: '10px', margin: 0 }}>{program.title}</h4>
                  <p style={{ margin: '10px 0 0', opacity: 0.9 }}>{program.grades}</p>
                </div>
                <div style={{ padding: '25px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <p style={{ color: '#333', marginBottom: '20px', lineHeight: '1.6' }}>
                    {program.description}
                  </p>
                  <div style={{ marginBottom: '20px', flex: 1 }}>
                    {program.features.map((feature, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '10px' }}>
                        <CheckCircle size={18} color="#58A95B" style={{ marginRight: '10px', marginTop: '2px', flexShrink: 0 }} />
                        <span style={{ color: '#666', fontSize: '0.95rem' }}>{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{
                    borderTop: '1px solid #eee',
                    paddingTop: '15px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.9rem',
                    color: '#666',
                    flexWrap: 'wrap',
                    gap: '10px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Clock size={16} style={{ marginRight: '5px' }} />
                      {program.duration}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Users size={16} style={{ marginRight: '5px' }} />
                      {program.batch}
                    </div>
                  </div>
                </div>
                <div style={{ padding: '0 25px 25px' }}>
                  <button style={{
                    width: '100%',
                    backgroundColor: '#58A95B',
                    border: 'none',
                    padding: '12px',
                    fontWeight: '600',
                    borderRadius: '8px',
                    color: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#4a8f4d'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#58A95B'}>
                    Enroll Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section style={{ padding: '80px 20px', backgroundColor: '#ccccccff', color: 'dark' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
            fontWeight: 'bold',
            marginBottom: '3rem'
          }}>
            Success <span style={{ color: '#58A95B' }}>Stories</span>
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '30px'
          }}>
            {testimonials.map((testimonial, idx) => (
              <div key={idx} style={{
                backgroundColor: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '15px',
                padding: '30px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#58A95B';
                e.currentTarget.style.transform = 'translateY(-5px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#333';
                e.currentTarget.style.transform = 'translateY(0)';
              }}>
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: '#58A95B',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    fontSize: '1.8rem',
                    fontWeight: 'bold',
                    color: 'white'
                  }}>
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </div>
                </div>
                <p style={{ color: '#ddd', fontStyle: 'italic', marginBottom: '20px', lineHeight: '1.6' }}>
                  "{testimonial.text}"
                </p>
                <h6 style={{ color: 'white', fontWeight: 'bold', marginBottom: '5px' }}>{testimonial.name}</h6>
                <p style={{ color: '#58A95B', margin: 0, fontSize: '0.9rem' }}>{testimonial.exam}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '80px 20px',
        background: 'linear-gradient(135deg, #58A95B 0%, #4a8f4d 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', fontWeight: 'bold', marginBottom: '1.5rem' }}>
            Ready to Start Your Journey?
          </h2>
          <p style={{ fontSize: 'clamp(1rem, 2vw, 1.2rem)', marginBottom: '2rem', lineHeight: '1.6' }}>
            Book a free consultation session with our expert mentors and get a personalized roadmap for your success.
          </p>
          <button style={{
            backgroundColor: '#000000',
            border: 'none',
            padding: '15px 50px',
            fontSize: '1.1rem',
            fontWeight: '600',
            borderRadius: '8px',
            color: 'white',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#1a1a1a';
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = '#000000';
            e.target.style.transform = 'scale(1)';
          }}>
            Book Free Consultation
          </button>
        </div>
      </section>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 0.5;
          }
          50% {
            opacity: 0.8;
          }
        }

        * {
          box-sizing: border-box;
        }

        button {
          font-family: inherit;
        }

        @media (max-width: 768px) {
          section {
            padding: 60px 15px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PersonalCoaching;